import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxChip } from '@onyx/angular';
import {
  OnyxChipsComponent,
  OnyxInputLabelComponent,
  OnyxSectionComponent,
  OnyxToggleComponent,
} from '@onyx/angular';
import { map } from 'rxjs';
import { WEEKDAYS } from '../../../../common/constants/common/weekdays';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { Weekdays } from '../../../../common/interfaces/common/weekdays';

type DelegationDailyReturnsFormGroup = ReturnType<
  (typeof DelegationDailyReturnsFormComponent)['buildForm']
>;

@Component({
  selector: 'app-delegation-daily-returns-form',
  imports: [
    OnyxSectionComponent,
    OnyxToggleComponent,
    TranslatePipe,
    OnyxChipsComponent,
    ReactiveFormsModule,
    OnyxInputLabelComponent,
  ],
  templateUrl: './delegation-daily-returns-form.component.html',
  styleUrls: ['./delegation-daily-returns-form.component.scss'],
})
export class DelegationDailyReturnsFormComponent implements OnInit {
  private readonly injector = inject(Injector);

  public readonly form = input.required<DelegationDailyReturnsFormGroup>();

  protected readonly I18N = 'delegations.dailyReturnsForm';
  protected readonly CHIPS: OnyxChip<string>[] = WEEKDAYS.map((value) => ({
    name: `labels.${value}`,
    value,
  }));

  protected days = signal<string[]>([]);
  protected isInvalid!: Signal<boolean>;
  protected hasDailyReturnsChanges!: Signal<boolean>;

  public ngOnInit(): void {
    const { daysOfWeek, hasSelectedDays, hasDailyReturns } =
      this.form().controls;

    this.isInvalid = toSignal(
      hasSelectedDays.statusChanges.pipe(
        map(() => hasSelectedDays.invalid && hasSelectedDays.touched),
      ),
      { initialValue: false, injector: this.injector },
    );

    this.hasDailyReturnsChanges = toSignal(
      hasDailyReturns.valueChanges.pipe(map(() => hasDailyReturns.value)),
      { initialValue: false, injector: this.injector },
    );

    effect(
      () => {
        const selectedDays = Object.entries(daysOfWeek.getRawValue() ?? {})
          .filter(([_, value]) => value === true)
          .map(([key]) => key);
        this.days.set(selectedDays);
      },
      { injector: this.injector },
    );

    effect(
      () => {
        ValidationHelper.toggleControls([daysOfWeek, hasSelectedDays], {
          enabled: this.hasDailyReturnsChanges(),
          reset: true,
        });
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const selectedDays = this.days();
        if (!selectedDays) return;

        const result = this.CHIPS.reduce((acc, chip) => {
          acc[chip.value as keyof Weekdays] = selectedDays.includes(chip.value);
          return acc;
        }, {} as Weekdays);

        daysOfWeek.setValue(result);

        const hasSelectedDaysValue = Object.values(result).some(
          (value) => value,
        );
        hasSelectedDays.setValue(hasSelectedDaysValue);
      },
      { injector: this.injector },
    );
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      hasSelectedDays: fb.control<boolean>(true, [Validators.requiredTrue]),
      hasDailyReturns: fb.control<boolean>(false, [Validators.required]),
      daysOfWeek: fb.control<Weekdays | null>({
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false,
      }),
    });
  }
}
